import React, { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { motion } from 'framer-motion';
import { Terminal, ArrowRight, Container, ShieldCheck, Check } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';

import { FormikInput } from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../services/api';
import SEO from '../../components/shared/SEO';

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" /><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" /><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" /><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" /></svg>
);

const RegisterSchema = Yup.object().shape({
  name: Yup.string().min(2, 'Too Short!').required('Required'),
  email: Yup.string().email('Invalid email').required('Required'),
  password: Yup.string().min(8, 'Password must be 8 chars').required('Required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Required'),
  terms: Yup.boolean().oneOf([true], 'Required'), // Logic for terms validation
});

export default function Register() {
  const navigate = useNavigate();
  const { login } = useAuth();

  // const handleGoogleLogin = useGoogleLogin({
  //   onSuccess: async (tokenResponse) => {
  //     try {
  //       const data = await authAPI.googleAuth(tokenResponse.access_token);
  //       login(data);
  //       navigate('/dashboard');
  //     } catch (error) {
  //       console.error("Google Auth Flow Failed", error);
  //     }
  //   },
  //   onError: () => console.log("Google Login Failed"),
  // });

  const handleRegister = async (values, { setSubmitting }) => {
    try {
      const { confirmPassword, terms, ...apiData } = values; // Logic: Strip 'terms' before API call
      const data = await authAPI.register(apiData);
      login(data);
      navigate('/dashboard/home');
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <SEO title="Initialize Account | Join EnvDock" description="Create your secure vault today. Start syncing secrets across your development team in minutes." />
   
    <div className="min-h-screen flex bg-[#050505] text-white overflow-hidden relative font-sans">
      
      {/* --- Background Visuals --- */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-orange-600/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-red-600/5 rounded-full blur-[120px]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:32px_32px]" />
      </div>

      {/* --- Left Side: Branding & Visuals --- */}
      <div className="hidden lg:flex w-1/2 flex-col justify-center px-24 relative z-10 border-r border-white/5 bg-black/20 backdrop-blur-3xl">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
          <Link to="/" className="mb-8 block group">
            <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/20 group-hover:scale-105 transition-transform">
              <Container size={28} className="text-white" strokeWidth={2.5} />
            </div>
          </Link>
          
          <h1 className="text-6xl font-black mb-8  tracking-tighter leading-none">
            Join the 
            <span className="text-transparent px-2 bg-clip-text bg-gradient-to-r from-orange-400 to-red-600">EnvDock</span>
          </h1>
          
          <p className="text-xl text-gray-500 max-w-md leading-relaxed mb-8">
            Automate your secrets management with a workflow built for modern engineering teams.
          </p>

          
        </motion.div>
      </div>

      {/* --- Right Side: Form --- */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 relative z-10 overflow-y-auto">
        <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md"
        >
          <div className="mb-10 text-center lg:text-left">
            <h2 className="text-4xl font-black tracking-tighter text-white mb-3">Create Account</h2>
            <p className="text-gray-500 font-medium">Start your 3-month free trial today.</p>
          </div>

          <Formik
            initialValues={{ name: '', email: '', password: '', confirmPassword: '', terms: false }}
            validationSchema={RegisterSchema}
            onSubmit={handleRegister}
          >
            {({ isSubmitting, values, errors, touched }) => (
              <Form className="space-y-4">
                <FormikInput name="name" label="Full Name" placeholder="Jane Doe" type="text" />
                <FormikInput name="email" label="Email Address" placeholder="jane@company.com" type="email" />
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormikInput name="password" label="Password" placeholder="••••••••" type="password" />
                    <FormikInput name="confirmPassword" label="Confirm" placeholder="••••••••" type="password" />
                </div>

                {/* --- Terms & Conditions Checkbox --- */}
                <div className="pt-2">
                    <label className="flex items-center gap-3 cursor-pointer group">
                        <div className="relative flex items-center mt-1">
                            <Field 
                                type="checkbox" 
                                name="terms" 
                                className="peer h-5 w-5 appearance-none rounded-md border-2 border-white/10 bg-white/5 checked:border-orange-500 checked:bg-orange-500/20 transition-all cursor-pointer" 
                            />
                            <Check className="absolute h-4 w-4 text-orange-500 opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none left-0.5" strokeWidth={4} />
                        </div>
                        <span className="text-sm text-gray-500 leading-snug group-hover:text-gray-400 transition-colors items-center">
                            I accept the <Link to="/terms" className="text-orange-500 hover:underline">Terms of Service</Link> and <Link to="/privacypolicy" className="text-orange-500 hover:underline">Privacy Policy</Link>.
                        </span>
                    </label>
                    {errors.terms && touched.terms && <div className="text-xs text-red-500 mt-1 ml-8">{errors.terms}</div>}
                </div>

                <Button
                  type="submit"
                  className="w-full h-14 mt-6 bg-orange-600 hover:bg-orange-500 text-white font-black text-lg rounded-2xl transition-all shadow-xl shadow-white/5 disabled:opacity-30 disabled:grayscale"
                  disabled={isSubmitting || !values.terms} // Button disabled until terms accepted
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                        Syncing Account...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                        Complete Registration <ArrowRight size={20} />
                    </span>
                  )}
                </Button>
              </Form>
            )}
          </Formik>

          <div className="mt-10 pt-8 border-t border-white/5 flex flex-col items-center gap-6">
            <p className="text-gray-500 text-sm font-medium">
              Already a member?{' '}
              <Link to="/login" className="text-orange-500 hover:text-orange-400 font-bold transition-colors">
                Sign in
              </Link>
            </p>

            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-orange-500/5 border border-orange-500/10">
                <ShieldCheck size={14} className="text-orange-500" />
                <span className="text-[10px] font-black uppercase tracking-widest text-orange-500/80">
                    AES-256 Bit Encryption Standard
                </span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
     </>
  );
}